import React, { useEffect } from 'react'

import { UserMenu } from '@pb/components'
import { Breakpoints, isHmBrand, useMediaQuery } from '@pb/utils'
import { useExperiment, useFeature } from '@optimizely/react-sdk'
import classnames from 'classnames/bind'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation } from 'react-router-dom'

import optimizelyConstants from 'constants/optimizely'
import useAccountFeature from 'features/account/hooks/useAccountFeature'
import useOptimizelyToggle from 'features/account/hooks/useOptimizelyToggle'
import settingsSelectors from 'features/account/settings/duck/selectors'
import {
  operationFetchReviewItemsNumber,
  operationSetBadges,
  operationSetDisablement,
  operationSetVisibility,
} from 'features/account/user-menu/duck/operations'

import selectors from '../../duck/selectors'
import { filterMenuByToggles, createMenuEvents } from '../../helpers/userMenuHelpers'
import menuToggles from './menuToggles'
import styles from './UserMenu.scss'

const cn = classnames.bind(styles)

const UserMenuComponent: React.FC = () => {
  const dispatch = useDispatch()
  const {
    pages: {
      hub: { shouldShowClubCard },
    },
  } = useAccountFeature()
  const isLargeOrSmaller = useMediaQuery(Breakpoints.LARGE)
  const isShowStaticMenu = useMediaQuery(Breakpoints.XXL)

  const [isDonateVoucherEnabled, variables] = useFeature(optimizelyConstants.DONATE_VOUCHER)
  const [donateVoucherVariation] = useExperiment(optimizelyConstants.DONATE_VOUCHER_EXPERIMENT)
  const showDonateVoucher = isDonateVoucherEnabled && donateVoucherVariation === 'on'
  const isDonateVoucherBaseLine = isDonateVoucherEnabled && donateVoucherVariation === 'base_line'

  const userMenus = useSelector(selectors.getUserMenus)
  const shouldFetchReviewItemsNumber = useSelector(selectors.getShouldFetchReviewItemsNumber)

  const isReactPaymentsPageEnabled = useOptimizelyToggle({
    key: optimizelyConstants.ACCOUNT_PAYMENTS_REACT,
    internalToggle: 'crmma_payments_react_internal',
  })
  const isRateReviewEnabled = useOptimizelyToggle({
    key: optimizelyConstants.ACCOUNT_RATE_REVIEW,
  })

  const shouldShowCompleteProfileReminder = useSelector(
    settingsSelectors.getShowCompleteProfileReminder,
  )

  useEffect(() => {
    dispatch(operationSetBadges(shouldShowCompleteProfileReminder))
    dispatch(operationSetDisablement())
    dispatch(operationSetVisibility({ paymentFeatureFlag: isReactPaymentsPageEnabled }))
  }, [userMenus])

  useEffect(() => {
    if (shouldFetchReviewItemsNumber) {
      dispatch(operationFetchReviewItemsNumber(isRateReviewEnabled))
    }
  }, [shouldFetchReviewItemsNumber])

  const handleCTAClick = (event): void => {
    event.preventDefault()
  }

  const { translations } = variables as any
  const translatedUserMenus = userMenus
    .reduce(filterMenuByToggles(menuToggles), [])
    .map(createMenuEvents({ showDonateVoucher, isDonateVoucherBaseLine, translations, dispatch }))

  const currentRoute = useLocation().pathname
  const matchedMenuItem = userMenus[0].menuItems.find((userMenuItem) =>
    currentRoute.includes(userMenuItem.to),
  )

  return translatedUserMenus.map(({ heading, menuItems }) => (
    <div
      className={cn({
        userMenuWrapper: shouldShowClubCard,
        menuSpacing: !isHmBrand,
      })}
      key={heading}
    >
      {menuItems && (
        <UserMenu
          shouldShowMemberColor={isLargeOrSmaller}
          shouldShowStaticMenu={isShowStaticMenu}
          menuItems={menuItems}
          heading={heading}
          matchedMenuItem={matchedMenuItem}
          handleCTAClick={handleCTAClick}
        />
      )}
    </div>
  ))
}

export default React.memo(UserMenuComponent)
